import {
  getAcraEntityCountFromDb,
  loadAcraSsicCatalogFromDb,
  searchAcraEntitiesFromDb,
} from './acraEntitiesDb';
import {
  ACRA_BUCKET_DATASET_IDS,
  ACRA_RICH_DATASET_IDS,
  OTHER_UEN_AGENCIES_DATASET_ID,
  acraBucketDatasetId,
  looksLikeUen,
  resolveAcraBucketKey,
} from './acraBucketMap';
import { getDbSupabase, isSupabaseConfigured } from './supabase';

const API_BASE = 'https://api-production.data.gov.sg';
const DATASTORE_BASE = 'https://data.gov.sg/api/action/datastore_search';
const CACHE_KEY = 'bth-data-gov-sg-index-v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const PAGE_DELAY_MS = 550;
const ACRA_TOTAL_CACHE_KEY = 'bth-acra-entity-total-v1';
const ACRA_TOTAL_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const ACRA_TOTAL_FALLBACK = 717_227;

/** Legacy unified ACRA dataset (8 fields) — still used by account intel Bizfile lookup. */
export const ACRA_ENTITIES_RESOURCE_ID = 'd_3f960c10fed6145404ca7b821f263b87';

export type AcraAuditFirm = {
  uen: string;
  name: string;
};

export type AcraEntityRecord = {
  uen: string;
  entityName: string;
  entityType: string;
  businessConstitution: string;
  companyType: string;
  status: string;
  issuanceAgency: string;
  registrationDate: string;
  issueDate: string;
  addressType: string;
  block: string;
  street: string;
  levelNo: string;
  unitNo: string;
  buildingName: string;
  postalCode: string;
  otherAddressLine1: string;
  otherAddressLine2: string;
  accountDueDate: string;
  annualReturnDate: string;
  primarySsicCode: string;
  primarySsicDescription: string;
  primaryActivity: string;
  secondarySsicCode: string;
  secondarySsicDescription: string;
  secondaryActivity: string;
  noOfOfficers: string;
  formerNames: string[];
  auditFirms: AcraAuditFirm[];
};

export type AcraEntitySearchResult = {
  records: AcraEntityRecord[];
  total: number;
  offset: number;
  limit: number;
};

export type AcraSsicOption = {
  code: string;
  description: string;
};

export type AcraSsicFilterKind = 'primary' | 'secondary';

export type DataGovSgDatasetSummary = {
  datasetId: string;
  name: string;
  status: string;
  description?: string;
  format: string;
  lastUpdatedAt: string;
  createdAt: string;
  managedByAgencyName: string;
  coverageStart?: string;
  coverageEnd?: string;
};

export type DataGovSgDatasetMetadata = DataGovSgDatasetSummary & {
  managedBy?: string;
  collectionIds?: string[];
  contactEmails?: string[];
  datasetSize?: number;
  columnMetadata?: {
    order?: string[];
    map?: Record<string, string>;
  };
};

export type DataGovSgIndexCache = {
  fetchedAt: string;
  totalPages: number;
  datasets: DataGovSgDatasetSummary[];
};

export type DataGovSgDatastorePreview = {
  fields: { id: string; type: string }[];
  records: Record<string, unknown>[];
  total: number;
};

type ApiEnvelope<T> = {
  code?: number;
  data?: T;
  errorMsg?: string;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function asSummary(raw: unknown): DataGovSgDatasetSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const datasetId = String(row.datasetId ?? '').trim();
  const name = String(row.name ?? '').trim();
  if (!datasetId || !name) return null;
  return {
    datasetId,
    name,
    status: String(row.status ?? '').trim() || 'unknown',
    description: typeof row.description === 'string' ? row.description : undefined,
    format: String(row.format ?? '').trim() || '—',
    lastUpdatedAt: String(row.lastUpdatedAt ?? '').trim(),
    createdAt: String(row.createdAt ?? '').trim(),
    managedByAgencyName: String(row.managedByAgencyName ?? row.managedBy ?? '').trim() || '—',
    coverageStart: typeof row.coverageStart === 'string' ? row.coverageStart : undefined,
    coverageEnd: typeof row.coverageEnd === 'string' ? row.coverageEnd : undefined,
  };
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`data.gov.sg request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function dataGovSgDatasetUrl(datasetId: string): string {
  return `https://data.gov.sg/datasets/${encodeURIComponent(datasetId)}/view`;
}

export function readDataGovSgIndexCache(): DataGovSgIndexCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DataGovSgIndexCache;
    if (!parsed?.fetchedAt || !Array.isArray(parsed.datasets)) return null;
    const age = Date.now() - new Date(parsed.fetchedAt).getTime();
    if (!Number.isFinite(age) || age > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeDataGovSgIndexCache(cache: DataGovSgIndexCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore quota errors — page still works without persistence.
  }
}

export async function fetchDataGovSgDatasetsPage(page: number): Promise<{
  datasets: DataGovSgDatasetSummary[];
  totalPages: number;
}> {
  const json = await fetchJson<ApiEnvelope<{ datasets?: unknown[]; pages?: number }>>(
    `${API_BASE}/v2/public/api/datasets?page=${Math.max(1, page)}`,
  );
  if (json.code !== 0 || !json.data) {
    throw new Error(json.errorMsg || 'Failed to load datasets.');
  }
  const datasets = (json.data.datasets ?? [])
    .map(asSummary)
    .filter((row): row is DataGovSgDatasetSummary => Boolean(row));
  return {
    datasets,
    totalPages: Math.max(1, Number(json.data.pages) || 1),
  };
}

export async function fetchDataGovSgDatasetMetadata(
  datasetId: string,
): Promise<DataGovSgDatasetMetadata> {
  const json = await fetchJson<ApiEnvelope<Record<string, unknown>>>(
    `${API_BASE}/v2/public/api/datasets/${encodeURIComponent(datasetId)}/metadata`,
  );
  if (json.code !== 0 || !json.data) {
    throw new Error(json.errorMsg || 'Failed to load dataset metadata.');
  }
  const summary = asSummary(json.data);
  if (!summary) throw new Error('Invalid dataset metadata.');
  return {
    ...summary,
    managedBy: typeof json.data.managedBy === 'string' ? json.data.managedBy : undefined,
    collectionIds: Array.isArray(json.data.collectionIds)
      ? json.data.collectionIds.map(String)
      : undefined,
    contactEmails: Array.isArray(json.data.contactEmails)
      ? json.data.contactEmails.map(String)
      : undefined,
    datasetSize: typeof json.data.datasetSize === 'number' ? json.data.datasetSize : undefined,
    columnMetadata:
      json.data.columnMetadata && typeof json.data.columnMetadata === 'object'
        ? (json.data.columnMetadata as DataGovSgDatasetMetadata['columnMetadata'])
        : undefined,
  };
}

export async function fetchDataGovSgDatastorePreview(
  datasetId: string,
  limit = 10,
): Promise<DataGovSgDatastorePreview> {
  const url = new URL(DATASTORE_BASE);
  url.searchParams.set('resource_id', datasetId);
  url.searchParams.set('limit', String(Math.min(Math.max(limit, 1), 100)));

  const json = await fetchJson<{
    success?: boolean;
    result?: {
      fields?: { id: string; type: string }[];
      records?: Record<string, unknown>[];
      total?: number;
    };
  }>(url.toString());

  if (!json.success || !json.result) {
    throw new Error('Preview unavailable for this dataset.');
  }

  return {
    fields: json.result.fields ?? [],
    records: json.result.records ?? [],
    total: Number(json.result.total) || 0,
  };
}

export async function loadDataGovSgIndex(opts?: {
  force?: boolean;
  onProgress?: (loadedPages: number, totalPages: number, datasets: DataGovSgDatasetSummary[]) => void;
  signal?: AbortSignal;
}): Promise<DataGovSgIndexCache> {
  if (!opts?.force) {
    const cached = readDataGovSgIndexCache();
    if (cached) return cached;
  }

  const first = await fetchDataGovSgDatasetsPage(1);
  const totalPages = first.totalPages;
  const datasets = [...first.datasets];
  opts?.onProgress?.(1, totalPages, datasets);

  for (let page = 2; page <= totalPages; page += 1) {
    if (opts?.signal?.aborted) {
      throw new Error('Index sync cancelled.');
    }
    await sleep(PAGE_DELAY_MS);
    const next = await fetchDataGovSgDatasetsPage(page);
    datasets.push(...next.datasets);
    opts?.onProgress?.(page, totalPages, [...datasets]);
  }

  const cache: DataGovSgIndexCache = {
    fetchedAt: new Date().toISOString(),
    totalPages,
    datasets,
  };
  writeDataGovSgIndexCache(cache);
  return cache;
}

export function matchesDataGovSgSearch(dataset: DataGovSgDatasetSummary, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    dataset.name,
    dataset.description,
    dataset.managedByAgencyName,
    dataset.format,
    dataset.status,
    dataset.datasetId,
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join('\n')
    .includes(q);
}

export function sortDataGovSgDatasets(
  datasets: DataGovSgDatasetSummary[],
  sort: 'updated' | 'name' | 'agency',
): DataGovSgDatasetSummary[] {
  const list = [...datasets];
  if (sort === 'name') {
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }
  if (sort === 'agency') {
    list.sort((a, b) => {
      const agency = a.managedByAgencyName.localeCompare(b.managedByAgencyName);
      return agency !== 0 ? agency : a.name.localeCompare(b.name);
    });
    return list;
  }
  list.sort((a, b) => {
    const updated = (b.lastUpdatedAt || '').localeCompare(a.lastUpdatedAt || '');
    return updated !== 0 ? updated : a.name.localeCompare(b.name);
  });
  return list;
}

export function formatDataGovSgBytes(bytes?: number): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function pickField(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') return String(value).trim();
  }
  return '';
}

function cleanAcraValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'na') return '';
  return trimmed;
}

/** Convert ACRA ALL-CAPS address text to Proper Case (keeps #01-12 and postal codes). */
export function toProperAddressCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  return trimmed.replace(/[A-Za-z0-9#]+/g, (token) => {
    if (/^#/.test(token)) return token; // unit no. e.g. #01-12
    if (/^\d+$/.test(token)) return token; // block / postal digits
    if (/^singapore$/i.test(token)) return 'Singapore';
    // Mixed tokens like 11A / PTE keep letter casing tidy
    return token.replace(/[A-Za-z]+/g, (letters) => {
      if (letters.length <= 2 && letters === letters.toUpperCase() && /^(PTE|LTD|LLP|PLC|UE)$/i.test(letters)) {
        return letters.toUpperCase();
      }
      return letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase();
    });
  });
}

export function parseAcraEntityRecord(row: Record<string, unknown>): AcraEntityRecord {
  const formerNames: string[] = [];
  for (let index = 1; index <= 15; index += 1) {
    const value = cleanAcraValue(
      pickField(row, `former_entity_name${index}`, `FORMER_ENTITY_NAME${index}`),
    );
    if (value) formerNames.push(value);
  }

  const auditFirms: AcraAuditFirm[] = [];
  for (let index = 1; index <= 5; index += 1) {
    const uen = cleanAcraValue(
      pickField(row, `uen_of_audit_firm${index}`, `UEN_OF_AUDIT_FIRM${index}`),
    );
    const name = cleanAcraValue(
      pickField(row, `name_of_audit_firm${index}`, `NAME_OF_AUDIT_FIRM${index}`),
    );
    if (uen || name) auditFirms.push({ uen, name });
  }

  return {
    uen: pickField(row, 'uen', 'UEN'),
    entityName: pickField(row, 'entityName', 'entity_name', 'ENTITY_NAME'),
    entityType: pickField(
      row,
      'entityType',
      'entity_type_desc',
      'ENTITY_TYPE_DESCRIPTION',
      'entity_type_description',
    ),
    businessConstitution: pickField(
      row,
      'businessConstitution',
      'business_constitution_description',
      'BUSINESS_CONSTITUTION_DESCRIPTION',
    ),
    companyType: pickField(row, 'companyType', 'company_type_description', 'COMPANY_TYPE_DESCRIPTION'),
    status: pickField(
      row,
      'status',
      'uen_status_desc',
      'ENTITY_STATUS_DESCRIPTION',
      'entity_status_description',
    ),
    issuanceAgency: pickField(
      row,
      'issuanceAgency',
      'issuance_agency_desc',
      'ISSUANCE_AGENCY_ID',
      'issuance_agency_id',
    ),
    registrationDate: (() => {
      const registered = pickField(
        row,
        'registrationDate',
        'registration_incorporation_date',
        'REGISTRATION_INCORPORATION_DATE',
      );
      if (registered) return registered;
      return pickField(row, 'issueDate', 'uen_issue_date', 'UEN_ISSUE_DATE');
    })(),
    issueDate: pickField(row, 'issueDate', 'uen_issue_date', 'UEN_ISSUE_DATE'),
    addressType: pickField(row, 'addressType', 'address_type', 'ADDRESS_TYPE'),
    block: pickField(row, 'block', 'BLOCK'),
    street: pickField(row, 'street', 'reg_street_name', 'STREET_NAME', 'street_name'),
    levelNo: pickField(row, 'levelNo', 'level_no', 'LEVEL_NO'),
    unitNo: pickField(row, 'unitNo', 'unit_no', 'UNIT_NO'),
    buildingName: pickField(row, 'buildingName', 'building_name', 'BUILDING_NAME'),
    postalCode: pickField(row, 'postalCode', 'reg_postal_code', 'POSTAL_CODE', 'postal_code'),
    otherAddressLine1: pickField(row, 'otherAddressLine1', 'other_address_line1', 'OTHER_ADDRESS_LINE1'),
    otherAddressLine2: pickField(row, 'otherAddressLine2', 'other_address_line2', 'OTHER_ADDRESS_LINE2'),
    accountDueDate: pickField(row, 'accountDueDate', 'account_due_date', 'ACCOUNT_DUE_DATE'),
    annualReturnDate: pickField(row, 'annualReturnDate', 'annual_return_date', 'ANNUAL_RETURN_DATE'),
    primarySsicCode: pickField(row, 'primarySsicCode', 'primary_ssic_code', 'PRIMARY_SSIC_CODE'),
    primarySsicDescription: pickField(
      row,
      'primarySsicDescription',
      'primary_ssic_description',
      'PRIMARY_SSIC_DESCRIPTION',
    ),
    primaryActivity: pickField(
      row,
      'primaryActivity',
      'primary_user_described_activity',
      'PRIMARY_USER_DESCRIBED_ACTIVITY',
    ),
    secondarySsicCode: pickField(row, 'secondarySsicCode', 'secondary_ssic_code', 'SECONDARY_SSIC_CODE'),
    secondarySsicDescription: pickField(
      row,
      'secondarySsicDescription',
      'secondary_ssic_description',
      'SECONDARY_SSIC_DESCRIPTION',
    ),
    secondaryActivity: pickField(
      row,
      'secondaryActivity',
      'secondary_user_described_activity',
      'SECONDARY_USER_DESCRIBED_ACTIVITY',
    ),
    noOfOfficers: pickField(row, 'noOfOfficers', 'no_of_officers', 'NO_OF_OFFICERS'),
    formerNames,
    auditFirms,
  };
}

export function formatAcraAddress(row: Pick<
  AcraEntityRecord,
  | 'block'
  | 'street'
  | 'levelNo'
  | 'unitNo'
  | 'buildingName'
  | 'postalCode'
  | 'otherAddressLine1'
  | 'otherAddressLine2'
>): string {
  const clean = (value?: string) => {
    const trimmed = value?.trim() ?? '';
    if (!trimmed || trimmed.toLowerCase() === 'na') return '';
    return trimmed;
  };

  const streetLine = [clean(row.block), clean(row.street)].filter(Boolean).join(' ').trim();
  const level = clean(row.levelNo);
  const unit = clean(row.unitNo);
  const unitLine =
    level || unit
      ? `#${[level, unit].filter(Boolean).join('-').replace(/^#/, '')}`
      : '';
  const locality = [streetLine, unitLine, clean(row.buildingName)].filter(Boolean).join(', ');
  const postal = clean(row.postalCode) ? `Singapore ${clean(row.postalCode)}` : '';
  const parts = [locality, clean(row.otherAddressLine1), clean(row.otherAddressLine2), postal].filter(
    Boolean,
  );
  const raw = parts.join(', ') || '—';
  return raw === '—' ? raw : toProperAddressCase(raw);
}

export function formatAcraSsicShort(
  row: Pick<AcraEntityRecord, 'primarySsicCode' | 'primarySsicDescription'>,
): { code: string; description: string } {
  const code = cleanSsicCode(row.primarySsicCode);
  const description = cleanSsicDescription(row.primarySsicDescription);
  return { code, description };
}

export function formatAcraSsic(row: Pick<AcraEntityRecord, 'primarySsicCode' | 'primarySsicDescription'>): string {
  const code = cleanSsicCode(row.primarySsicCode);
  const description = cleanSsicDescription(row.primarySsicDescription);
  if (code && description) return `${code} — ${description}`;
  return description || code || '—';
}

function cleanSsicCode(value?: string): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || trimmed.toLowerCase() === 'na') return '';
  return trimmed;
}

function cleanSsicDescription(value?: string): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || trimmed.toLowerCase() === 'na') return '';
  return trimmed;
}

export function buildAcraSsicOptions(
  records: AcraEntityRecord[],
  kind: AcraSsicFilterKind,
): AcraSsicOption[] {
  const map = new Map<string, string>();
  for (const row of records) {
    const code = cleanSsicCode(kind === 'primary' ? row.primarySsicCode : row.secondarySsicCode);
    if (!code) continue;
    const description = cleanSsicDescription(
      kind === 'primary' ? row.primarySsicDescription : row.secondarySsicDescription,
    );
    if (!map.has(code)) map.set(code, description);
  }
  return [...map.entries()]
    .map(([code, description]) => ({ code, description }))
    .sort((a, b) => a.code.localeCompare(b.code));
}

export function formatAcraSsicOption(option: AcraSsicOption): string {
  return option.description ? `${option.code} — ${option.description}` : option.code;
}

function buildSsicApiFilters(opts: {
  primarySsicCode?: string;
  secondarySsicCode?: string;
}): Record<string, string> | undefined {
  const filters: Record<string, string> = {};
  const primary = cleanSsicCode(opts.primarySsicCode);
  const secondary = cleanSsicCode(opts.secondarySsicCode);
  if (primary) filters.primary_ssic_code = primary;
  if (secondary) filters.secondary_ssic_code = secondary;
  return Object.keys(filters).length > 0 ? filters : undefined;
}

function hasActiveSsicFilter(opts: {
  primarySsicCode?: string;
  secondarySsicCode?: string;
}): boolean {
  return Boolean(buildSsicApiFilters(opts));
}

function isFetchAborted(err: unknown, signal?: AbortSignal | null): boolean {
  if (signal?.aborted) return true;
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  return err instanceof TypeError && err.message === 'Failed to fetch' && Boolean(signal?.aborted);
}

async function fetchJsonWithRetry<T>(
  url: string,
  init?: RequestInit,
  retries = 2,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (init?.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    try {
      const res = await fetch(url, init);
      if (!res.ok) {
        throw new Error(`data.gov.sg request failed (${res.status})`);
      }
      return (await res.json()) as T;
    } catch (err) {
      lastError = err;
      if (isFetchAborted(err, init?.signal)) throw err;
      if (attempt >= retries) break;
      await sleep(350 * (attempt + 1));
    }
  }
  throw lastError instanceof Error ? lastError : new Error('data.gov.sg request failed.');
}

async function searchAcraViaEdge(opts: {
  query: string;
  offset?: number;
  limit?: number;
  primarySsicCode?: string;
  secondarySsicCode?: string;
}): Promise<AcraEntitySearchResult | null> {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await getDbSupabase().functions.invoke('account-intel', {
    body: {
      searchAcra: {
        query: opts.query.trim(),
        offset: opts.offset ?? 0,
        limit: opts.limit ?? 25,
        primarySsicCode: cleanSsicCode(opts.primarySsicCode) || undefined,
        secondarySsicCode: cleanSsicCode(opts.secondarySsicCode) || undefined,
      },
    },
  });
  if (error) return null;
  const payload = data as {
    records?: Record<string, unknown>[];
    total?: number;
    offset?: number;
    limit?: number;
    error?: string;
  } | null;
  if (!payload || payload.error || !Array.isArray(payload.records)) return null;
  return {
    records: payload.records.map(parseAcraEntityRecord),
    total: Number(payload.total) || payload.records.length,
    offset: Number(payload.offset) || 0,
    limit: Number(payload.limit) || opts.limit || 25,
  };
}

const ACRA_NAME_STOP_WORDS = new Set([
  'pte',
  'ltd',
  'limited',
  'company',
  'co',
  'enterprise',
  'enterprises',
  'the',
  'and',
  'group',
  'holding',
  'private',
  'inc',
  'llc',
  'pl',
  'sdn',
  'bhd',
]);

function normAcraName(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function significantAcraTokens(query: string): string[] {
  return normAcraName(query)
    .split(' ')
    .filter((token) => token.length >= 2 && !ACRA_NAME_STOP_WORDS.has(token.toLowerCase()));
}

function rankAcraEntities(records: AcraEntityRecord[], query: string): AcraEntityRecord[] {
  const target = normAcraName(query);
  const tokens = significantAcraTokens(query);

  return [...records]
    .map((record) => {
      const name = normAcraName(record.entityName);
      let score = 0;
      if (name === target) score += 1000;
      else if (target && name.startsWith(target)) score += 500;
      else if (target && name.includes(target)) score += 300;

      const tokenHits = tokens.filter((token) => name.includes(token)).length;
      score += tokenHits * 40;
      if (tokens.length > 0 && tokenHits < tokens.length) score -= 500;

      if (/live|register/i.test(record.status)) score += 8;
      return { record, score };
    })
    .filter(({ score }) => score > 0 || tokens.length === 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.record.entityName.localeCompare(b.record.entityName);
    })
    .map(({ record }) => record);
}

export function isAcraCorporateDataset(dataset: DataGovSgDatasetSummary): boolean {
  const name = dataset.name.trim().toLowerCase();
  if (dataset.datasetId === ACRA_ENTITIES_RESOURCE_ID) return true;
  if (ACRA_RICH_DATASET_IDS.includes(dataset.datasetId)) return true;
  if (name === 'entities registered with acra') return true;
  if (name.includes('acra information on corporate entities')) return true;
  if (name.includes('acra') && name.includes('corporate entit')) return true;
  return false;
}

async function fetchAcraDatastore(opts: {
  resourceId: string;
  query?: string;
  uen?: string;
  filters?: Record<string, string>;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<{ records: AcraEntityRecord[]; total: number }> {
  const url = new URL(DATASTORE_BASE);
  url.searchParams.set('resource_id', opts.resourceId);

  const filterObj: Record<string, string> = { ...(opts.filters ?? {}) };
  if (opts.uen) {
    filterObj.uen = opts.uen.trim().toUpperCase();
  }

  const hasFilters = Object.keys(filterObj).length > 0;
  const hasQuery = Boolean(opts.query && opts.query.length >= 2);

  if (hasQuery) {
    url.searchParams.set('q', JSON.stringify({ entity_name: opts.query }));
  } else if (!hasFilters) {
    return { records: [], total: 0 };
  }

  if (hasFilters) {
    url.searchParams.set('filters', JSON.stringify(filterObj));
  }

  url.searchParams.set('limit', String(opts.limit ?? 25));
  url.searchParams.set('offset', String(opts.offset ?? 0));

  const json = await fetchJsonWithRetry<{
    success?: boolean;
    result?: {
      records?: Record<string, unknown>[];
      total?: number;
    };
  }>(url.toString(), {
    headers: { Accept: 'application/json' },
    signal: opts.signal,
  });

  if (!json.success || !json.result) {
    throw new Error('ACRA search failed.');
  }

  return {
    records: (json.result.records ?? []).map(parseAcraEntityRecord),
    total: Number(json.result.total) || 0,
  };
}

async function searchAcraByUen(
  uen: string,
  signal?: AbortSignal,
): Promise<AcraEntitySearchResult> {
  const normalized = uen.trim().toUpperCase();
  const bucketKey = resolveAcraBucketKey(normalized);
  const primaryId = ACRA_BUCKET_DATASET_IDS[bucketKey] ?? ACRA_BUCKET_DATASET_IDS.Others;
  try {
    const primary = await fetchAcraDatastore({
      resourceId: primaryId,
      uen: normalized,
      limit: 1,
      signal,
    });
    if (primary.records[0]) {
      return { records: primary.records, total: 1, offset: 0, limit: 1 };
    }
  } catch (err) {
    if (isFetchAborted(err, signal)) throw err;
  }

  for (const resourceId of ACRA_RICH_DATASET_IDS) {
    if (resourceId === primaryId) continue;
    try {
      const result = await fetchAcraDatastore({
        resourceId,
        uen: normalized,
        limit: 1,
        signal,
      });
      if (result.records[0]) {
        return { records: result.records, total: 1, offset: 0, limit: 1 };
      }
    } catch {
      // try next bucket
    }
  }

  try {
    const other = await fetchAcraDatastore({
      resourceId: OTHER_UEN_AGENCIES_DATASET_ID,
      uen: normalized,
      limit: 1,
      signal,
    });
    if (other.records[0]) {
      return { records: other.records, total: 1, offset: 0, limit: 1 };
    }
  } catch (err) {
    if (isFetchAborted(err, signal)) throw err;
  }

  const edge = await searchAcraViaEdge({ query: normalized, limit: 1, offset: 0 });
  if (edge?.records[0]) return edge;

  return { records: [], total: 0, offset: 0, limit: 1 };
}

async function searchAcraBySsic(opts: {
  primarySsicCode?: string;
  secondarySsicCode?: string;
  offset: number;
  limit: number;
  signal?: AbortSignal;
}): Promise<AcraEntitySearchResult> {
  const filters = buildSsicApiFilters(opts);
  if (!filters) {
    return { records: [], total: 0, offset: opts.offset, limit: opts.limit };
  }

  let total = 0;
  const merged: AcraEntityRecord[] = [];
  const targetEnd = opts.offset + opts.limit;

  for (let index = 0; index < ACRA_RICH_DATASET_IDS.length; index += 4) {
    const batch = ACRA_RICH_DATASET_IDS.slice(index, index + 4);
    const batchResults = await Promise.all(
      batch.map(async (resourceId) => {
        try {
          return await fetchAcraDatastore({
            resourceId,
            filters,
            limit: 1,
            offset: 0,
            signal: opts.signal,
          });
        } catch (err) {
          if (isFetchAborted(err, opts.signal)) throw err;
          return { records: [], total: 0 };
        }
      }),
    );
    total += batchResults.reduce((sum, row) => sum + row.total, 0);
  }

  for (const resourceId of ACRA_RICH_DATASET_IDS) {
    if (merged.length >= targetEnd) break;
    try {
      const result = await fetchAcraDatastore({
        resourceId,
        filters,
        limit: Math.min(100, targetEnd - merged.length + opts.limit),
        offset: 0,
        signal: opts.signal,
      });
      merged.push(...result.records);
    } catch (err) {
      if (isFetchAborted(err, opts.signal)) throw err;
    }
  }

  const records = merged.slice(opts.offset, opts.offset + opts.limit);
  return {
    records,
    total,
    offset: opts.offset,
    limit: opts.limit,
  };
}

export async function searchAcraEntities(opts: {
  query: string;
  offset?: number;
  limit?: number;
  primarySsicCode?: string;
  secondarySsicCode?: string;
  signal?: AbortSignal;
}): Promise<AcraEntitySearchResult> {
  const query = opts.query.trim();
  const offset = Math.max(0, opts.offset ?? 0);
  const limit = Math.min(Math.max(opts.limit ?? 25, 1), 100);
  const ssicFilters = buildSsicApiFilters(opts);
  const ssicOnly = hasActiveSsicFilter(opts) && !query;

  if (!query && !ssicOnly) {
    return { records: [], total: 0, offset, limit };
  }

  const local = await searchAcraEntitiesFromDb({
    query,
    offset,
    limit,
    primarySsicCode: opts.primarySsicCode,
    secondarySsicCode: opts.secondarySsicCode,
  });
  if (local) {
    const records = query && !looksLikeUen(query)
      ? rankAcraEntities(local.records, query)
      : local.records;
    return { ...local, records };
  }

  if (ssicOnly) {
    try {
      return await searchAcraBySsic({
        primarySsicCode: opts.primarySsicCode,
        secondarySsicCode: opts.secondarySsicCode,
        offset,
        limit,
        signal: opts.signal,
      });
    } catch (err) {
      if (isFetchAborted(err, opts.signal)) throw err;
      const edge = await searchAcraViaEdge({
        query: '',
        offset,
        limit,
        primarySsicCode: opts.primarySsicCode,
        secondarySsicCode: opts.secondarySsicCode,
      });
      if (edge) return edge;
      throw err;
    }
  }

  if (looksLikeUen(query)) {
    return searchAcraByUen(query, opts.signal);
  }

  if (query.length < 2) {
    return { records: [], total: 0, offset, limit };
  }

  const resourceId = acraBucketDatasetId(query);
  let result: { records: AcraEntityRecord[]; total: number };
  try {
    result = await fetchAcraDatastore({
      resourceId,
      query: ssicFilters ? undefined : query,
      filters: ssicFilters,
      offset,
      limit,
      signal: opts.signal,
    });
  } catch (err) {
    if (isFetchAborted(err, opts.signal)) throw err;
    const edge = await searchAcraViaEdge({
      query,
      offset,
      limit,
      primarySsicCode: opts.primarySsicCode,
      secondarySsicCode: opts.secondarySsicCode,
    });
    if (edge) return { ...edge, records: rankAcraEntities(edge.records, query) };
    throw err;
  }

  // Also search societies / charities / clubs when corporate letter bucket is thin.
  if (!ssicFilters && result.records.length < limit) {
    try {
      const other = await fetchAcraDatastore({
        resourceId: OTHER_UEN_AGENCIES_DATASET_ID,
        query,
        offset: 0,
        limit,
        signal: opts.signal,
      });
      const seen = new Set(result.records.map((row) => row.uen.toUpperCase()));
      const merged = [...result.records];
      for (const row of other.records) {
        const key = row.uen.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(row);
      }
      result = { records: merged, total: Math.max(result.total, other.total) };
    } catch (err) {
      if (isFetchAborted(err, opts.signal)) throw err;
    }
  }

  let records = ssicFilters ? result.records : rankAcraEntities(result.records, query);
  if (ssicFilters) {
    records = rankAcraEntities(records, query);
  }

  return { records: records.slice(0, limit), total: result.total, offset, limit };
}

export function acraBizfileSearchUrl(uen: string): string {
  const q = uen.trim().toUpperCase();
  return `https://www.bizfile.gov.sg/buy-info/search/results?query=${encodeURIComponent(q)}`;
}

function readAcraTotalCache(): number | null {
  try {
    const raw = localStorage.getItem(ACRA_TOTAL_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { total?: number; fetchedAt?: string };
    if (!parsed?.fetchedAt || !Number.isFinite(parsed.total)) return null;
    const age = Date.now() - new Date(parsed.fetchedAt).getTime();
    if (!Number.isFinite(age) || age > ACRA_TOTAL_CACHE_TTL_MS) return null;
    return parsed.total ?? null;
  } catch {
    return null;
  }
}

function writeAcraTotalCache(total: number): void {
  try {
    localStorage.setItem(
      ACRA_TOTAL_CACHE_KEY,
      JSON.stringify({ total, fetchedAt: new Date().toISOString() }),
    );
  } catch {
    // ignore quota errors
  }
}

async function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = [];
  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    const batchResults = await Promise.all(batch.map(mapper));
    results.push(...batchResults);
    if (index + batchSize < items.length) {
      await sleep(120);
    }
  }
  return results;
}

export async function fetchAcraEntityTotalCount(opts?: { background?: boolean }): Promise<number> {
  const localCount = await getAcraEntityCountFromDb();
  if (localCount != null && localCount > 0) {
    writeAcraTotalCache(localCount);
    return localCount;
  }

  const cached = readAcraTotalCache();
  if (cached != null) {
    if (!opts?.background) return cached;
    void refreshAcraEntityTotalCount();
    return cached;
  }
  return refreshAcraEntityTotalCount();
}

async function refreshAcraEntityTotalCount(): Promise<number> {
  const previews = await mapInBatches(ACRA_RICH_DATASET_IDS, 4, async (datasetId) =>
    fetchDataGovSgDatastorePreview(datasetId, 1).catch(() => ({ total: 0, fields: [], records: [] })),
  );
  const total = previews.reduce((sum, preview) => sum + preview.total, 0);
  const resolved = total > 0 ? total : ACRA_TOTAL_FALLBACK;
  writeAcraTotalCache(resolved);
  return resolved;
}

const ACRA_SSIC_CACHE_KEY = 'bth-acra-ssic-catalog-v1';
const ACRA_SSIC_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type AcraSsicCatalog = {
  fetchedAt: string;
  primary: AcraSsicOption[];
  secondary: AcraSsicOption[];
};

function readAcraSsicCatalogCache(): AcraSsicCatalog | null {
  try {
    const raw = localStorage.getItem(ACRA_SSIC_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AcraSsicCatalog;
    if (!parsed?.fetchedAt || !Array.isArray(parsed.primary) || !Array.isArray(parsed.secondary)) {
      return null;
    }
    const age = Date.now() - new Date(parsed.fetchedAt).getTime();
    if (!Number.isFinite(age) || age > ACRA_SSIC_CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeAcraSsicCatalogCache(catalog: AcraSsicCatalog): void {
  try {
    localStorage.setItem(ACRA_SSIC_CACHE_KEY, JSON.stringify(catalog));
  } catch {
    // ignore quota errors
  }
}

function mergeSsicOptionMaps(
  primary: Map<string, string>,
  secondary: Map<string, string>,
  records: AcraEntityRecord[],
): void {
  for (const row of records) {
    const primaryCode = cleanSsicCode(row.primarySsicCode);
    if (primaryCode && !primary.has(primaryCode)) {
      primary.set(primaryCode, cleanSsicDescription(row.primarySsicDescription));
    }
    const secondaryCode = cleanSsicCode(row.secondarySsicCode);
    if (secondaryCode && !secondary.has(secondaryCode)) {
      secondary.set(secondaryCode, cleanSsicDescription(row.secondarySsicDescription));
    }
  }
}

function mapsToCatalog(
  primary: Map<string, string>,
  secondary: Map<string, string>,
): Pick<AcraSsicCatalog, 'primary' | 'secondary'> {
  const toOptions = (map: Map<string, string>) =>
    [...map.entries()]
      .map(([code, description]) => ({ code, description }))
      .sort((a, b) => a.code.localeCompare(b.code));

  return {
    primary: toOptions(primary),
    secondary: toOptions(secondary),
  };
}

export async function loadAcraSsicCatalog(opts?: { force?: boolean }): Promise<AcraSsicCatalog> {
  if (!opts?.force) {
    const cached = readAcraSsicCatalogCache();
    if (cached) return cached;

    const local = await loadAcraSsicCatalogFromDb();
    if (local && (local.primary.length > 0 || local.secondary.length > 0)) {
      writeAcraSsicCatalogCache(local);
      return local;
    }
  }

  const primary = new Map<string, string>();
  const secondary = new Map<string, string>();

  await mapInBatches(ACRA_RICH_DATASET_IDS, 3, async (datasetId) => {
    try {
      const preview = await fetchDataGovSgDatastorePreview(datasetId, 100);
      mergeSsicOptionMaps(primary, secondary, preview.records.map(parseAcraEntityRecord));
    } catch {
      // skip bucket on failure
    }
  });

  const catalog: AcraSsicCatalog = {
    fetchedAt: new Date().toISOString(),
    ...mapsToCatalog(primary, secondary),
  };
  writeAcraSsicCatalogCache(catalog);
  return catalog;
}
